/**
 * Floor-set notation for scheduled actions: a union of mathematical intervals
 * such as `[1,5),[10,12]` naming the floors an action targets.
 *
 * Grammar (ASCII only; whitespace between tokens is insignificant):
 *
 *     spec     := member (',' member)*      -- comma at bracket depth 0
 *     member   := interval | integer        -- bare integer = singleton
 *     interval := ('['|'(') integer ',' integer (']'|')')
 *     integer  := '-'? DIGIT+
 *
 * `[` and `]` include the bound, `(` and `)` exclude it, so `[1,5)` is
 * {1,2,3,4} and `(1,5]` is {2,3,4,5}.
 *
 * Validation is deliberately STRICT: a spec is rejected, never coerced and never
 * silently emptied. In particular a syntactically perfect but VACUOUS interval
 * (`[3,3)`, `(3,3]`, `(3,4)`) is an error, because storing one would produce a
 * scheduled action that can never fire — a silent no-op is the exact failure
 * this notation exists to prevent.
 *
 * Cardinality is computed from the bounds ARITHMETICALLY, before any expansion,
 * so a pathological interval such as `[-2147483648,2147483647)` is rejected in
 * constant time instead of being materialised into an array that hangs the tab.
 *
 * This is a deliberate mirror of LiftCloud's `utils/floorspec` Go package. The
 * two are kept in agreement by `flatbuffer_schemas/fixtures/floor_spec_conformance.json`,
 * which both test suites load — see `__tests__/floorSpec.test.ts`.
 */

/**
 * Stable, machine-readable rejection reasons, shared with the Go validator in
 * LiftCloud (utils/floorspec) and with the conformance fixture. A union of
 * string literals rather than an enum, so a code is written as the literal it
 * is on the wire and a typo is still a compile error.
 */
export type FloorSpecErrorCode =
  | "EMPTY_SPEC"
  | "EMPTY_MEMBER"
  | "UNBALANCED_BRACKET"
  | "NESTED_BRACKET"
  | "MALFORMED_INTERVAL"
  | "INVALID_INTEGER"
  | "BOUND_OUT_OF_RANGE"
  | "BOUND_ORDER"
  | "EMPTY_INTERVAL"
  | "TOO_MANY_FLOORS"
  | "TRAILING_CHARACTERS"
  | "FLOOR_NOT_IN_BANK";

/**
 * Maximum floors one spec may resolve to. A scheduled action fans out to one
 * Control command per (bank, lift, floor) tuple, so an unbounded floor set is a
 * dispatch storm against the edge.
 */
export const MAX_FLOORS_PER_SPEC = 512;

/** Bounds are int32, matching `TimedAction.position`. */
export const MIN_BOUND = -2147483648;
export const MAX_BOUND = 2147483647;

/**
 * Matches an optionally negative ASCII decimal integer and nothing else. Used
 * INSTEAD of `parseInt`, which silently truncates `"1.5"` to 1 and `"1abc"` to
 * 1 — turning a typo into a valid-looking floor.
 */
const STRICT_INTEGER = /^-?[0-9]+$/;

/** Whitespace, matching Go's `unicode.IsSpace` (U+00A0 included in both). */
const WHITESPACE = /\s/;

export interface FloorSpecError {
  code: FloorSpecErrorCode;
  /**
   * 0-based index of the first character of the offending token. For an empty or
   * whitespace-only member it is the member's raw start index, which may equal
   * the input length when the spec ends in a comma.
   */
  offset: number;
  /** Human-readable explanation. */
  detail: string;
  /** The offending floor. Present only for `FLOOR_NOT_IN_BANK`. */
  floor?: number;
}

/** A rejection. Shared by every internal step so failures propagate unchanged. */
export interface FloorSpecFailure {
  ok: false;
  error: FloorSpecError;
}

export type FloorSpecResult = { ok: true; floors: number[] } | FloorSpecFailure;

function fail(code: FloorSpecErrorCode, offset: number, detail: string): FloorSpecFailure {
  return { ok: false, error: { code, offset, detail } };
}

/** One comma-separated member plus where it started in the original spec. */
interface Member {
  start: number;
  text: string;
}

/**
 * Resolve a floor spec to a sorted, de-duplicated list of floor positions.
 *
 * An empty or whitespace-only spec is an ERROR, not an empty set. Callers that
 * treat an absent spec as "fall back to the scalar position" must check for
 * absence themselves — the distinction between "not configured" and "configured
 * to nothing" is exactly what must not be blurred.
 *
 * @param domain Floor positions the target banks actually have, as the union
 *   across every selected bank. An empty array skips the check, which is what is
 *   wanted while the bank's floor list is still loading — validating against an
 *   unknown domain would reject every legal spec.
 */
export function parseFloorSpec(spec: string, domain: number[] = []): FloorSpecResult {
  if (spec.trim() === "") {
    return fail("EMPTY_SPEC", 0, "Specification is empty");
  }

  const split = splitMembers(spec);
  if (!split.ok) return split;

  const seen = new Set<number>();

  for (let i = 0; i < split.members.length; i += 1) {
    const parsed = parseMember(split.members[i]);
    if (!parsed.ok) return parsed;

    parsed.floors.forEach((floor) => seen.add(floor));

    // Check the running union too, not only each member: ten members of 100
    // floors each are individually legal and collectively a dispatch storm.
    if (seen.size > MAX_FLOORS_PER_SPEC) {
      return fail(
        "TOO_MANY_FLOORS",
        0,
        `Specification resolves to more than ${MAX_FLOORS_PER_SPEC} floors`,
      );
    }
  }

  const floors = Array.from(seen).sort((a, b) => a - b);

  return checkDomain(floors, domain);
}

/** Convenience predicate for form validators that only need pass/fail. */
export function isValidFloorSpec(spec: string, domain: number[] = []): boolean {
  return parseFloorSpec(spec, domain).ok;
}

type SplitResult = { ok: true; members: Member[] } | FloorSpecFailure;

/**
 * Cut the spec at bracket-depth-0 commas. The comma is both the union separator
 * and the interval bound separator, which is unambiguous precisely because
 * intervals are bracket-delimited: a comma inside brackets is at depth 1, a
 * comma between members is at depth 0.
 */
function splitMembers(spec: string): SplitResult {
  const members: Member[] = [];
  let depth = 0;
  let openerAt = 0;
  let memberFrom = 0;

  for (let i = 0; i < spec.length; i += 1) {
    const ch = spec[i];

    if (ch === "[" || ch === "(") {
      depth += 1;
      if (depth > 1) {
        return fail("NESTED_BRACKET", i, "Nested interval brackets are not allowed");
      }
      openerAt = i;
    } else if (ch === "]" || ch === ")") {
      depth -= 1;
      if (depth < 0) {
        return fail("UNBALANCED_BRACKET", i, "Closing bracket without a matching opening bracket");
      }
    } else if (ch === "," && depth === 0) {
      members.push({ start: memberFrom, text: spec.slice(memberFrom, i) });
      memberFrom = i + 1;
    }
  }

  if (depth !== 0) {
    return fail("UNBALANCED_BRACKET", openerAt, "Opening bracket is never closed");
  }

  members.push({ start: memberFrom, text: spec.slice(memberFrom) });

  return { ok: true, members };
}

type MemberResult = { ok: true; floors: number[] } | FloorSpecFailure;

/** Resolve one union member — an interval or a bare singleton — to its floors. */
function parseMember(member: Member): MemberResult {
  const trimmed = member.text.trim();
  if (trimmed === "") {
    return fail("EMPTY_MEMBER", member.start, "Empty specification member");
  }

  const at = member.start + member.text.indexOf(trimmed);

  if (trimmed[0] === "[" || trimmed[0] === "(") {
    return parseInterval(trimmed, at);
  }

  const bound = parseBound(trimmed, at);
  if (!bound.ok) return bound;

  return { ok: true, floors: [bound.value] };
}

/** Resolve a bracketed interval. `at` is the absolute offset of its opener. */
function parseInterval(member: string, at: number): MemberResult {
  const closeIdx = member.search(/[\])]/);
  if (closeIdx < 0) {
    return fail("UNBALANCED_BRACKET", at, "Opening bracket is never closed");
  }

  // Anything after the closing bracket other than whitespace is junk. Without
  // this check "[1,2)x" would parse as a clean interval and the operator would
  // never learn the "x" was ignored.
  const rest = member.slice(closeIdx + 1);
  if (rest.trim() !== "") {
    const offset = at + closeIdx + 1 + rest.search(/\S/);

    return fail("TRAILING_CHARACTERS", offset, "Unexpected characters after the closing bracket");
  }

  const bounds = resolveBounds(member, closeIdx, at);
  if (!bounds.ok) return bounds;

  const floors: number[] = [];
  for (let f = bounds.first; f <= bounds.last; f += 1) floors.push(f);

  return { ok: true, floors };
}

type BoundsResult = { ok: true; first: number; last: number } | FloorSpecFailure;

/**
 * Turn the two bound tokens of a bracketed interval into the inclusive
 * [first, last] range it denotes, applying the bracket exclusivity rules and
 * rejecting an inverted, vacuous or oversized interval.
 */
function resolveBounds(member: string, closeIdx: number, at: number): BoundsResult {
  const parts = member.slice(1, closeIdx).split(",");
  if (parts.length !== 2) {
    return fail(
      "MALFORMED_INTERVAL",
      at,
      "An interval must contain exactly two bounds separated by a comma",
    );
  }

  // Absolute offset of each bound token's first non-whitespace character: the
  // lower starts one past the opening bracket, the upper one past their comma.
  const low = parseBound(parts[0].trim(), boundOffset(at + 1, parts[0]));
  if (!low.ok) return low;

  const high = parseBound(parts[1].trim(), boundOffset(at + 1 + parts[0].length + 1, parts[1]));
  if (!high.ok) return high;

  if (low.value > high.value) {
    return fail("BOUND_ORDER", at, "Lower bound is greater than the upper bound");
  }

  // Exclusive bounds shrink the interval inwards.
  let first = low.value;
  let last = high.value;
  if (member[0] === "(") first += 1;
  if (member[closeIdx] === ")") last -= 1;

  if (first > last) {
    return fail(
      "EMPTY_INTERVAL",
      at,
      "Interval contains no floors; exclusive bounds made it empty",
    );
  }

  // Derived from the bounds, NOT from a materialised array — this is what keeps
  // "[-2147483648,2147483647)" a fast rejection instead of a hung tab.
  const count = last - first + 1;
  if (count > MAX_FLOORS_PER_SPEC) {
    return fail(
      "TOO_MANY_FLOORS",
      at,
      `Interval resolves to ${count} floors, more than the ${MAX_FLOORS_PER_SPEC} allowed`,
    );
  }

  return { ok: true, first, last };
}

/**
 * Absolute offset of the first non-whitespace character of a bound token,
 * falling back to the token's own start when it is empty or all whitespace.
 */
function boundOffset(start: number, token: string): number {
  for (let i = 0; i < token.length; i += 1) {
    if (!WHITESPACE.test(token[i])) return start + i;
  }

  return start;
}

type BoundResult = { ok: true; value: number } | FloorSpecFailure;

/**
 * Convert one bound token to a floor position, rejecting anything that is not a
 * plain ASCII integer inside the int32 range.
 */
function parseBound(token: string, at: number): BoundResult {
  if (!STRICT_INTEGER.test(token)) {
    return fail("INVALID_INTEGER", at, `"${token}" is not an integer`);
  }

  const value = Number(token);
  if (!Number.isSafeInteger(value) || value < MIN_BOUND || value > MAX_BOUND) {
    return fail(
      "BOUND_OUT_OF_RANGE",
      at,
      `Bound ${token} is outside the range [${MIN_BOUND}, ${MAX_BOUND}]`,
    );
  }

  return { ok: true, value };
}

/**
 * Reject the spec when a resolved floor is not a floor of any targeted bank.
 * `floors` is already sorted, so the reported floor is deterministically the
 * lowest missing one — matching the Go validator.
 *
 * This runs LAST, after syntax, ordering, vacuity and cardinality, so a typo is
 * reported as the malformed thing it is rather than the more confusing "floor
 * not in bank".
 */
function checkDomain(floors: number[], domain: number[]): FloorSpecResult {
  if (domain.length === 0) return { ok: true, floors };

  const known = new Set(domain);
  // floors is sorted, so find() reports the LOWEST missing floor — the same one
  // the Go validator reports, which the shared domain vectors assert.
  const missing = floors.find((floor) => !known.has(floor));

  if (missing !== undefined) {
    return {
      ok: false,
      error: {
        code: "FLOOR_NOT_IN_BANK",
        offset: 0,
        floor: missing,
        detail: `Floor ${missing} is not present in any targeted bank`,
      },
    };
  }

  return { ok: true, floors };
}

/**
 * Serialise a floor list back into the canonical spec the grammar above
 * accepts — the exact inverse of `parseFloorSpec` for any set it can express.
 *
 * This is NOT `describeFloors`. That one renders for humans: it separates with
 * ", " and joins runs with an EN DASH, neither of which the grammar admits, so
 * feeding its output back in is a parse error. Two functions exist because the
 * two audiences genuinely differ — one is read, the other is re-parsed — and a
 * single "pretty enough to also be valid" format would quietly be neither.
 *
 * Runs of three or more consecutive floors collapse to an INCLUSIVE interval
 * (`[1,4]`); shorter runs stay as bare singletons, because "[1,2]" is longer
 * than "1,2" and no clearer. An empty list yields the empty string, which the
 * caller must read as "no floors configured" — `parseFloorSpec` rejects it
 * rather than treating it as the empty set, deliberately.
 */
export function floorsToSpec(floors: number[]): string {
  if (floors.length === 0) return "";

  const sorted = Array.from(new Set(floors)).sort((a, b) => a - b);
  const parts: string[] = [];
  let runStart = sorted[0];
  let previous = sorted[0];

  const flush = (): void => {
    const length = previous - runStart + 1;
    if (length >= 3) {
      parts.push(`[${runStart},${previous}]`);
      return;
    }

    for (let f = runStart; f <= previous; f += 1) parts.push(String(f));
  };

  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] !== previous + 1) {
      flush();
      runStart = sorted[i];
    }

    previous = sorted[i];
  }

  flush();

  return parts.join(",");
}

/**
 * Render a resolved floor list for display, collapsing runs into ranges:
 * [1,2,3,5,9,10] becomes "1–3, 5, 9–10". Used by the editor's live preview so an
 * operator can see what a spec actually expands to before saving.
 */
export function describeFloors(floors: number[]): string {
  if (floors.length === 0) return "";

  const parts: string[] = [];
  let runStart = floors[0];
  let previous = floors[0];

  const flush = (): void => {
    if (runStart === previous) parts.push(String(runStart));
    else if (previous === runStart + 1) parts.push(`${runStart}, ${previous}`);
    else parts.push(`${runStart}–${previous}`);
  };

  for (let i = 1; i < floors.length; i += 1) {
    if (floors[i] !== previous + 1) {
      flush();
      runStart = floors[i];
    }

    previous = floors[i];
  }

  flush();

  return parts.join(", ");
}
